import { useSyncExternalStore } from 'react';
import { Box, ButtonBase, Link, Tooltip, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { PrintOutlined } from '@mui/icons-material';
import { effectivePrinter, loadSavedPrinter, loadSystemPrinter, subscribePrinter } from '../util/printerPicker';
import { openPrinterSettings } from '../util/qzTray';
import { useAppSocket } from '../providers/AppSocketContext';
import { useUI } from '../providers/MoleUIContext';
import { useTranslation } from 'react-i18next';

// Snapshot as a string so useSyncExternalStore sees a stable value between changes.
const printerSnapshot = () => `${loadSavedPrinter() ?? ''}\u0000${loadSystemPrinter() ?? ''}`;

const PrinterStatus = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    useSyncExternalStore(subscribePrinter, printerSnapshot);
    const { printer, source } = effectivePrinter();
    const tooltip = printer
        ? t(source === 'app' ? 'common:printer.status.app' : 'common:printer.status.system', { printer })
        : t('common:printer.status.noneHint');

    return (
        <Tooltip title={tooltip}>
            <ButtonBase
                onClick={() => void openPrinterSettings()}
                data-testid="printer-status"
                sx={{
                    color: printer ? 'text.secondary' : 'warning.main',
                    fontSize: '0.8125rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.25,
                    py: 0.5,
                    borderRadius: 999,
                    border: '1px solid',
                    borderColor: alpha(printer ? theme.palette.divider : theme.palette.warning.main, 0.6),
                    whiteSpace: 'nowrap',
                    flexShrink: 1,
                    minWidth: 0,
                    maxWidth: 240,
                    '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.6) },
                }}
            >
                <PrintOutlined sx={{ fontSize: 16, flexShrink: 0 }} />
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: source === 'app' ? 600 : 500 }}>
                    {printer ?? t('common:printer.status.none')}
                </Box>
            </ButtonBase>
        </Tooltip>
    );
};

const BottomBar = () => {
    const { connected } = useAppSocket();
    const { statusMessage } = useUI();
    const { t } = useTranslation();
    const theme = useTheme();
    const themeColor = theme.palette.primary.main;
    const isDark = theme.palette.mode === 'dark';
    const hasStatus = statusMessage != null && statusMessage !== '';

    return (
        <Box
            className="footer-bar"
            sx={{
                px: { xs: 1.25, sm: 2 },
                py: 0.75,
                minHeight: 'var(--footer-height)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: { xs: 1, md: 1.5 },
                bgcolor: alpha(theme.palette.background.paper, 0.94),
                backdropFilter: 'blur(10px)',
                borderTop: `1px solid ${alpha(themeColor, 0.28)}`,
                boxShadow: `0 -4px 20px ${alpha(theme.palette.common.black, isDark ? 0.28 : 0.06)}`,
            }}
        >
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, minWidth: 0, flexShrink: 0 }}>
                <Box
                    className="footer-status"
                    sx={{
                        color: 'text.secondary',
                        fontSize: '0.8125rem',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.25,
                        py: 0.5,
                        borderRadius: 999,
                        bgcolor: alpha(connected ? theme.palette.success.main : theme.palette.error.main, 0.1),
                        border: '1px solid',
                        borderColor: alpha(connected ? theme.palette.success.main : theme.palette.error.main, 0.35),
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                    }}
                >
                    <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 10, height: 10 }}>
                        {connected && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    bgcolor: 'var(--online-color)',
                                    opacity: 0.35,
                                    animation: 'status-beacon var(--beacon-duration) var(--motion-ease-emphasized) infinite',
                                }}
                            />
                        )}
                        <Box
                            component="span"
                            sx={{
                                display: 'inline-block',
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: connected ? 'var(--online-color)' : 'var(--offline-color)',
                            }}
                            data-testid="network-indicator-dot"
                            aria-label={connected ? t('common:status.online') : t('common:status.offline')}
                        />
                    </Box>
                    <Box component="span" data-testid="network-indicator-text" sx={{ fontWeight: 600, letterSpacing: 0.2 }}>
                        {connected ? t('common:status.online') : t('common:status.offline')}
                    </Box>
                </Box>
                <PrinterStatus />
            </Box>

            <Box
                className="footer-message"
                role="status"
                aria-live="polite"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 0,
                    flex: 1,
                    justifyContent: 'center',
                    px: { xs: 1, sm: 1.5 },
                    py: 0.45,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: hasStatus ? alpha(themeColor, 0.3) : alpha(theme.palette.divider, 0.8),
                    borderStyle: hasStatus ? 'solid' : 'dashed',
                    bgcolor: hasStatus ? alpha(themeColor, 0.07) : alpha(theme.palette.action.hover, 0.35),
                    maxWidth: { xs: '100%', md: 'min(640px, 55vw)' },
                }}
            >
                <Box
                    component="span"
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.8125rem',
                        fontWeight: hasStatus ? 600 : 500,
                        color: hasStatus ? 'text.primary' : 'text.secondary',
                        width: '100%',
                        textAlign: 'center',
                        display: 'block',
                    }}
                >
                    {hasStatus ? statusMessage : t('common:footer.ready')}
                </Box>
            </Box>

            <Box
                className="footer-brand"
                sx={{
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    display: { xs: 'none', sm: 'block' },
                }}
            >
                {t('common:footer.poweredBy')}{' '}
                <Link
                    href="https://www.moleculesoft.net"
                    target="_blank"
                    rel="noreferrer"
                    underline="hover"
                    sx={{ fontWeight: 600, color: 'inherit' }}
                >
                    MOLeCULE Soft
                </Link>
                {' '}&copy; {new Date().getFullYear()}
            </Box>
        </Box>
    );
};

export default BottomBar;